import dayjs from 'dayjs';
import { cloneData, DataModel, DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../dto/dto.js';
import { BusinessModuleRef } from './business-module-ref.js';
import { toEnumFromString, fromEnumAsString } from '../../serialization/enum-utils.js';
import { TimeZone } from '../../time/timezone.js';
import { MapUtils } from '../../utility/map-utils.js';
import { VersionRange } from '../core/version-range.js';

export enum OrderCompletionSchedulerTaskStatus {
  Running = 'RUNNING',
  Idle = 'IDLE',
}

export class OrderCompletionSchedulerTask extends DataModel {
  static readonly TYPE_ID: string = 'ede0c55225b34648b0b88ba905aba4cf';
  static readonly SCHEMA_VERSION: number = 2;

  private constructor(
    public readonly uniqueId: string,
    public readonly creationDate: dayjs.Dayjs,
    public readonly status: OrderCompletionSchedulerTaskStatus,
    public readonly businessRef: BusinessModuleRef,
    public readonly eventId: string | null,
    public readonly numberOfRequestsPerBatch: number,
  ) {
    super();
  }

  get $typeId(): string {
    return OrderCompletionSchedulerTask.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(OrderCompletionSchedulerTask.SCHEMA_VERSION);
  }

  static create(args: {
    uniqueId: string,
    creationDate: dayjs.Dayjs,
    status: OrderCompletionSchedulerTaskStatus,
    businessRef: BusinessModuleRef,
    eventId: string | null,
    numberOfRequestsPerBatch: number,
  }): OrderCompletionSchedulerTask {
    return new OrderCompletionSchedulerTask(
        args.uniqueId,
        args.creationDate,
        args.status,
        args.businessRef,
        args.eventId,
        args.numberOfRequestsPerBatch,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): OrderCompletionSchedulerTask {
    return new OrderCompletionSchedulerTask(
        MapUtils.getString(map, 'uniqueId'),
        context.decodeDate(MapUtils.getInteger(map, 'creationDate')),
        toEnumFromString(MapUtils.getString(map, '$status'), OrderCompletionSchedulerTaskStatus),
        BusinessModuleRef.mustDecode(MapUtils.getString(map, 'businessRef')),
        MapUtils.getStringOrNull(map, 'eventId'),
        MapUtils.getInteger(map, 'numberOfRequestsPerBatch'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'creationDate': context.encodeDate(this.creationDate),
      '$status': fromEnumAsString(this.status),
      'businessRef': this.businessRef.encode(),
      'eventId': this.eventId,
      'numberOfRequestsPerBatch': this.numberOfRequestsPerBatch,
    };
  }

  clone(): this {
    return new OrderCompletionSchedulerTask(
        this.uniqueId,
        TimeZone.cloneDate(this.creationDate),
        this.status,
        cloneData(this.businessRef),
        this.eventId,
        this.numberOfRequestsPerBatch,
    ) as this;
  }

  isSame(other: this): boolean {
    if(this === other) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        other.creationDate === this.creationDate &&
        other.status === this.status &&
        isSameData(other.businessRef, this.businessRef) &&
        other.eventId === this.eventId &&
        other.numberOfRequestsPerBatch === this.numberOfRequestsPerBatch;
  }

  copyAsIdle(): this {
    return new OrderCompletionSchedulerTask(
        this.uniqueId,
        TimeZone.cloneDate(this.creationDate),
        OrderCompletionSchedulerTaskStatus.Idle,
        cloneData(this.businessRef),
        this.eventId,
        this.numberOfRequestsPerBatch,
    ) as this;
  }

  copyAsRunning(numberOfRequestsPerBatch?: number): this {
    return new OrderCompletionSchedulerTask(
        this.uniqueId,
        TimeZone.cloneDate(this.creationDate),
        OrderCompletionSchedulerTaskStatus.Running,
        cloneData(this.businessRef),
        this.eventId,
        numberOfRequestsPerBatch ?? this.numberOfRequestsPerBatch,
    ) as this;
  }
}

export class OrderCompletionSchedulerTaskBuilder extends DataModelBuilder<OrderCompletionSchedulerTask> {
  build(context: SerializationContext, map: SerializedData): OrderCompletionSchedulerTask {
    return OrderCompletionSchedulerTask.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(OrderCompletionSchedulerTask.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(OrderCompletionSchedulerTask.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version `eventId` was made nullable
    if(version < 2) version = 2;

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }

  migrateFromCurrentSchemaVersion(
      context: SerializationContext,
      model: OrderCompletionSchedulerTask,
      map: SerializedData
  ): SerializedData {
    const versionRange = MapUtils.getVersionRange(map);
    let version = versionRange.from;

    if(version >= 2) {
      if(model.eventId == null) return map;

      version = 1;
      map['$schema_version'] = VersionRange.encodedBetween(1, 2);
    }

    return map;
  }
}
